import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, TextInput } from '@forecastify/ui';
import { apiUrl } from '../../api';
import { useT } from '../../i18n';
import { SIGN_IN_PATH } from '../../routes';
import { AuthField, AuthFrame } from './auth-frame';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `/forgot-password` — ask for a reset link. C18.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The other end of the mockup's `Elfelejtetted?`. The mockup draws the link and not this page, so
 * `docs/design-system/` decides everything the login card does not already settle — which in
 * practice means `AuthFrame`, since a request form is the same card with one field in it.
 *
 * ⚠️⚠️ **IT SAYS THE SAME THING WHETHER OR NOT THE ADDRESS HAS AN ACCOUNT, AND THAT SENTENCE IS THE
 * FEATURE.** The API answers `202` either way (`auth.controller.ts`, `password-reset.service.ts`,
 * and `0028` all carry the rule) — and a screen that rendered *"we could not find that address"*
 * would hand back through the UI precisely what three layers below it refused to leak. A
 * forgotten-password form is the easiest existence oracle in any product.
 *
 * ⚠️ **So the confirmation is phrased to be TRUE in both cases.** *"If there is an account for this
 * address, a link is on its way"* is honest; *"we have sent you an email"* is a lie half the time,
 * and it is the half where somebody sits waiting for a mail that will never arrive.
 *
 * ⚠️ **`signedOutOk`, because the whole audience is people who cannot sign in.** Without it `Gate`
 * would render the sign-in form at this address — the same reason `/invite/:token` needs the flag.
 */
export function ForgotPasswordScreen() {
  const t = useT();

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const submit = (): void => {
    void (async () => {
      setBusy(true);
      setFailure(null);

      /* ⚠️ Plain `fetch`, not `session.request`: there is no session, and that helper attaches a
       * bearer token and a workspace header which do not exist. `invite-screen.tsx` does the same
       * for the same reason. */
      const response = await fetch(apiUrl('/auth/password/forgot'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      }).catch(() => null);

      setBusy(false);

      /**
       * ⚠️ **A NETWORK failure is reported; a 4xx is NOT.** The server answers `202` for a valid
       * address and for a malformed one, so the only thing a non-2xx here can mean is that the API
       * is down — which is worth saying, because it is the one case where trying again later is the
       * right advice. Anything else would be reconstructing the oracle.
       */
      if (response === null) {
        setFailure(t('error.unavailable'));
        return;
      }
      setSent(true);
    })();
  };

  return (
    <AuthFrame screen="forgot-password" title={t('forgot.title')}>
      {sent ? (
        <>
          {/* ⚠️ `role="status"`, so the change is announced: the form has been replaced by a
              confirmation, and a screen-reader user is otherwise left on a page that silently
              became a different page. */}
          <p role="status" className="text-[13px] leading-relaxed">
            {t('forgot.sent')}
          </p>
          <p className="text-muted-foreground text-[12.5px]">{t('forgot.sentNote')}</p>
          <Link
            to={SIGN_IN_PATH}
            className="text-muted-foreground hover:text-foreground text-[11.5px] underline"
          >
            {t('forgot.backToSignIn')}
          </Link>
        </>
      ) : (
        <form
          className="flex flex-col gap-3.5"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <p className="text-muted-foreground text-[12.5px] leading-relaxed">{t('forgot.lead')}</p>

          <AuthField label={t('signIn.email')}>
            <TextInput
              type="email"
              autoComplete="username"
              placeholder={t('signIn.emailPlaceholder')}
              autoFocus
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </AuthField>

          {failure !== null && (
            <p role="alert" className="text-destructive text-[12px]">
              {failure}
            </p>
          )}

          <Button type="submit" size="lg" className="w-full" disabled={busy || email.trim() === ''}>
            {busy ? t('forgot.working') : t('forgot.submit')}
          </Button>

          {/* ⚠️ A `<Link>`, not a button — `button.tsx`: *"a control that NAVIGATES is not a
              button"*. Somebody who remembered their password mid-form should not have to use Back. */}
          <Link
            to={SIGN_IN_PATH}
            className="text-muted-foreground hover:text-foreground text-center text-[11.5px] underline"
          >
            {t('forgot.backToSignIn')}
          </Link>
        </form>
      )}
    </AuthFrame>
  );
}
